import React, { useState, useEffect, useCallback } from 'react';
import { money as moneyOf } from '../utils/money';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Plus, X, Pencil, Trash2, Check, Undo2, AlertTriangle, Calendar, Truck } from 'lucide-react';
// Shared with JQBookings so both booking forms handle money identically.
import { stripThousands, formatThousands, handleMoneyInput } from '../utils/money';
// Shared with JQBookings — the two tables use the same paired-cell language.
import { formatDateShort, formatAmount } from '../utils/bookingFormat';
import DepositPaymentsForm, { buildDepositFormData } from './DepositPaymentsForm';
import DepositReceiptReview from './DepositReceiptReview';

const API_BASE = '/api';

// The six house venues, worded and ordered exactly as constants/meetingOptions VENUES.
const VENUES = ['W Factory', 'W Nexus (1 Utama)', 'W Timber', 'W in the Sky', 'W Nexus (Kelana Jaya)', 'W Nexus (Kuala Lumpur)'];
const VENUE_OTHER = 'Other (specify)';
const EVENT_TYPES = ['Wedding', 'Corporate', 'Private']; // match the scorecard Done-Deals columns

const money = (v) => moneyOf(v ?? 0);
const EMPTY = { transfer_date: '', event_date: '', event_type: 'Wedding', lead_type: 'lead_generate', venue: 'W Factory', venue_other: '', package: '', client_name: '', invoice_number: '', forecast_pax: '', contract_value: '', deposit_amount: '' };
const leadLabel = (t) => t === 'hunting' ? 'Hunting' : 'Lead Gen';

// Zen's operational tracker plus the August-effective 70/30 commission view.
function ZenBookings({ month }) {
  const { token, user, isSuperadmin } = useAuth();
  // Gate on the linked salesperson (stable), not the editable display name —
  // Zen's login is "Zen Ong" but his salesperson is "Zen". Fall back to name
  // for older tokens that predate salesperson_name.
  const isZen =
    (user?.salesperson_name || '').trim().toLowerCase() === 'zen' ||
    (user?.name || '').trim().toLowerCase() === 'zen';
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deliverModal, setDeliverModal] = useState(null);
  const [actualValue, setActualValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tierInfo, setTierInfo] = useState(null);
  const [paymentSeed, setPaymentSeed] = useState(null);
  const [paymentDraft, setPaymentDraft] = useState(null);
  const [paymentFormKey, setPaymentFormKey] = useState(0);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [editImportedDetails, setEditImportedDetails] = useState(false);
  const handleDepositChange = useCallback(value => setPaymentDraft(value), []);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [bookingsResponse, tierResponse] = await Promise.all([
        axios.get(`${API_BASE}/zen-bookings?month=${month}`, { headers }),
        axios.get(`${API_BASE}/zen-bookings/tier-rate?month=${month}`, { headers })
      ]);
      setBookings(bookingsResponse.data || []);
      setTierInfo(tierResponse.data || null);
    } catch (e) { setError(e.response?.data?.error || 'Failed to load bookings'); }
    setLoading(false);
  }, [month, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setShowForm(true); setError('');
    setPaymentSeed(null); setPaymentDraft(null); setEditImportedDetails(false); setPaymentFormKey(key => key + 1); };
  const openEdit = async (b) => {
    setPaymentSeed(null); setPaymentDraft(null); setEditImportedDetails(false);
    setEditing(b);
    setForm({
      transfer_date: (b.transfer_date || '').slice(0, 10), event_date: (b.event_date || '').slice(0, 10),
      event_type: b.event_type || 'Wedding', lead_type: b.lead_type || 'lead_generate',
      venue: VENUES.includes(b.venue) ? b.venue : VENUE_OTHER, venue_other: VENUES.includes(b.venue) ? '' : (b.venue || ''), package: b.package || '',
      client_name: b.client_name || '', invoice_number: b.invoice_number || '',
      forecast_pax: b.forecast_pax ?? '', contract_value: b.contract_value ?? '',
      // Rows that predate the deposit_amount migration have none — leave the field
      // blank rather than showing a 0 that was never collected.
      deposit_amount: b.deposit_amount != null ? formatThousands(b.deposit_amount) : ''
    });
    setShowForm(true); setError('');
    if (b.receipt_required) {
      setPaymentLoading(true);
      try {
        const response = await axios.get(`${API_BASE}/zen-bookings/${b.id}/payments`, { headers });
        setPaymentSeed(response.data);
        setPaymentFormKey(key => key + 1);
      } catch (e) { setError(e.response?.data?.error || 'Failed to load deposit receipts'); }
      finally { setPaymentLoading(false); }
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    // "Other (specify)" → use the typed venue name as the booking's venue.
    const venue = form.venue === VENUE_OTHER ? (form.venue_other || '').trim() : form.venue;
    if (form.venue === VENUE_OTHER && !venue) { setError('Please specify the venue'); return; }
    const payload = { ...form, venue, deposit_amount: stripThousands(form.deposit_amount) };
    const future = !editing || editing.receipt_required;
    const attachmentOnly = editing?.deposit_entry_source === 'spreadsheet' && !editImportedDetails;
    if (future && (!paymentDraft?.valid || paymentLoading)) {
      setError('Enter the required deposit and attach a receipt for every payment.');
      return;
    }
    try {
      if (attachmentOnly) await axios.put(`${API_BASE}/zen-bookings/${editing.id}/payments`,
        buildDepositFormData({}, paymentDraft), { headers });
      else if (editing) await axios.put(`${API_BASE}/zen-bookings/${editing.id}`,
        future ? buildDepositFormData(payload, paymentDraft) : payload, { headers });
      else await axios.post(`${API_BASE}/zen-bookings`, buildDepositFormData(payload, paymentDraft), { headers });
      setShowForm(false); setForm(EMPTY); setEditing(null); setPaymentSeed(null); setPaymentDraft(null); load();
    } catch (e) { setError(e.response?.data?.error || 'Failed to save booking'); }
  };

  // Verification is the admin's half of this page: Zen keys bookings in, an admin
  // confirms the deposit landed. The routes have existed since the recorder shipped
  // (PUT /:id/verify and PUT /verify-all, both requireSuperadmin) but nothing here
  // ever called them, so the ACTIONS column rendered empty for an admin.
  const verifyOne = async (b, verified) => {
    setError('');
    try { await axios.put(`${API_BASE}/zen-bookings/${b.id}/verify`, { verified }, { headers }); load(); }
    catch (e) { setError(e.response?.data?.error || 'Failed to update verification'); }
  };

  const verifyAll = async () => {
    setError('');
    try { await axios.put(`${API_BASE}/zen-bookings/verify-all`, { month }, { headers }); load(); }
    catch (e) { setError(e.response?.data?.error || 'Failed to verify all'); }
  };

  const del = async (b) => {
    if (!window.confirm('Delete this booking?')) return;
    try { await axios.delete(`${API_BASE}/zen-bookings/${b.id}`, { headers }); load(); }
    catch (e) { setError(e.response?.data?.error || 'Failed to delete'); }
  };

  const deliver = async (booking) => {
    const actualNum = parseFloat(stripThousands(actualValue));
    if (!Number.isFinite(actualNum) || actualNum < 0) {
      setError('Actual final value must be a valid non-negative number');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const response = await axios.put(`${API_BASE}/zen-bookings/${booking.id}/deliver`, {
        actual_final_value: actualNum
      }, { headers });
      setSuccess(response.data.message || 'Delivery recorded');
      setDeliverModal(null);
      setActualValue('');
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to mark as delivered');
    } finally {
      setSubmitting(false);
    }
  };

  // Match JQ's month attribution: capture belongs to booking_month while the
  // final settlement belongs to delivered_month (the event month).
  const bookedThisMonth = bookings.filter(b => b.booking_month === month);
  const pendingBookings = bookedThisMonth.filter(b => b.delivery_status === 'pending');
  const deliveredBookings = bookings.filter(b => b.delivery_status === 'delivered' && b.delivered_month === month);
  const capturedTotal = bookedThisMonth.reduce((s, b) => s + Number(b.contract_value || 0), 0);
  const capturedCount = bookedThisMonth.length;
  // Captured broken down by how the deal was sourced. Both halves are computed over
  // bookedThisMonth — the same rows capturedTotal sums — so Lead Gen + Hunting is
  // always exactly Captured, never a near-miss against a different row set.
  // Anything not explicitly 'hunting' is a supplied lead, matching leadLabel().
  const huntingRows = bookedThisMonth.filter(b => b.lead_type === 'hunting');
  const leadGenRows = bookedThisMonth.filter(b => b.lead_type !== 'hunting');
  const sumValue = (rows) => rows.reduce((s, b) => s + Number(b.contract_value || 0), 0);
  const plural = (n) => `${n} booking${n === 1 ? '' : 's'}`;
  // Counts every booking on the list, matching what verify-all actually updates
  // (booking_month OR delivered_month = month), not just the captured ones.
  const unverifiedCount = bookings.filter(b => !b.verified).length;
  const eligibleThisMonth = bookedThisMonth.filter(b => b.booking_month >= '2026-08');
  const verifiedPaid70 = eligibleThisMonth.filter(b => b.verified).reduce((sum, b) => sum + Number(b.paid_70_percent || 0), 0);
  const pendingPaid70 = eligibleThisMonth.filter(b => !b.verified).reduce((sum, b) => sum + Number(b.paid_70_percent || 0), 0);
  const settlements = bookings
    .filter(b => b.delivery_status === 'delivered' && b.delivered_month === month)
    .reduce((sum, b) => sum + Number(b.settlement_amount || 0), 0);

  const card = { background: 'var(--rmc-cream)', border: '1px solid var(--rmc-line)', borderRadius: 12, padding: '14px 16px' };
  const lbl = { fontSize: 12, fontWeight: 700, color: 'var(--rmc-ink2)', textTransform: 'uppercase', letterSpacing: '.05em' };
  const inputS = { padding: '8px 10px', border: '1px solid var(--rmc-line)', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', width: '100%' };
  const btn = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid var(--rmc-amber)', background: 'var(--rmc-amber)', color: 'var(--rmc-ink)', fontWeight: 800, fontSize: 13, cursor: 'pointer' };
  const iconBtn = { display: 'inline-flex', padding: 6, borderRadius: 6, border: '1px solid var(--rmc-line)', background: 'var(--rmc-white)', cursor: 'pointer', color: 'var(--rmc-ink2)' };
  // Money-in, held in its own panel exactly as JQ's form does it: when the cash
  // landed (which sets the capture month) and how much of it. Green rather than
  // the form's amber, on a white ground so the panel sits above the card's cream
  // rather than being cut into it. Mirrors .deposit-panel in SalespersonDetail.css,
  // restated inline because this component carries no stylesheet.
  const depPanel = { marginBottom: 18, padding: '1.15rem', border: '1px solid color-mix(in srgb, var(--rmc-green-deep) 40%, transparent)', borderRadius: 8, background: 'var(--rmc-white)' };
  const depTitle = { display: 'block', marginBottom: '0.9rem', paddingBottom: '0.6rem', borderBottom: '1px solid color-mix(in srgb, var(--rmc-green-deep) 18%, transparent)', fontSize: '1.15rem', fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--rmc-green-deep)' };
  const depLbl = { ...lbl, color: 'var(--rmc-green-deep)' };
  const depInput = { ...inputS, background: 'var(--rmc-white)', borderColor: 'color-mix(in srgb, var(--rmc-green-deep) 35%, transparent)' };
  const hint = { display: 'block', marginTop: 4, fontSize: '0.73rem', fontWeight: 400, letterSpacing: 0, textTransform: 'none', color: 'var(--rmc-ink3)' };

  return (
    <div className="zen-bookings bookings-view">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--rmc-ink)' }}>Zen Bookings <span style={{ color: 'var(--rmc-ink2)', fontWeight: 600, fontSize: 15 }}>· {month}</span></h2>
        <div style={{ display: 'flex', gap: 8 }}>
          {isZen && <button style={btn} onClick={openCreate}><Plus size={15} /> Add Booking</button>}
          {isSuperadmin && unverifiedCount > 0 && (
            <button style={btn} onClick={verifyAll} title="Verify every unverified booking this month">
              <Check size={15} /> Verify all ({unverifiedCount})
            </button>
          )}
        </div>
      </div>

      {error && <div style={{ color: 'var(--rmc-coral)', marginBottom: 12, fontWeight: 600 }}>{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {isZen && month >= '2026-08' && tierInfo && (
        <div className="tier-rate-banner" style={{ marginBottom: 16 }}>
          <span>Eligible booked value: <strong>{money(tierInfo.cumulativeRevenue)}</strong></span>
          <span className="tier-rate-divider">|</span>
          <span>Current rate: <strong>{Number(tierInfo.tierRate || 0).toFixed(2)}%</strong></span>
          <span className="tier-rate-divider">|</span>
          <span>Verified 70%: <strong>{money(verifiedPaid70)}</strong></span>
          {pendingPaid70 > 0 && <span>Pending verification: <strong>{money(pendingPaid70)}</strong></span>}
          {settlements !== 0 && <span>Settlement: <strong>{money(settlements)}</strong></span>}
          <span className="tier-rate-note">Wedding, Private and Corporate</span>
        </div>
      )}
      {isZen && month < '2026-08' && (
        <div className="tier-rate-banner" style={{ marginBottom: 16 }}>
          <span>Commission scheme starts August 2026.</span>
        </div>
      )}

      {/* Laid out as the equation it is: the two sources, then the total they make.
          The operators are decorative — aria-hidden — so a screen reader hears three
          labelled figures rather than "plus" and "equals" between them. Card classes
          are the JQ page's (see .bookings-view in SalespersonDetail.css). */}
      <div className="sum-row">
        <div className="jq-summary-card">
          <p className="jq-summary-label">Lead Gen</p>
          <h4>{money(sumValue(leadGenRows))}</h4>
          <p className="jq-summary-count">{plural(leadGenRows.length)}</p>
        </div>
        <span className="sum-op" aria-hidden="true">+</span>
        <div className="jq-summary-card">
          <p className="jq-summary-label">Hunting</p>
          <h4>{money(sumValue(huntingRows))}</h4>
          <p className="jq-summary-count">{plural(huntingRows.length)}</p>
        </div>
        <span className="sum-op" aria-hidden="true">=</span>
        <div className="jq-summary-card highlight">
          <p className="jq-summary-label">Captured</p>
          <h4>{money(capturedTotal)}</h4>
          <p className="jq-summary-count">{plural(capturedCount)}</p>
        </div>
      </div>

      {showForm && (
        <form onSubmit={submit} style={{ ...card, marginBottom: 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <strong>{editing ? 'Edit Booking' : 'New Booking'}</strong>
            <button type="button" style={iconBtn} onClick={() => { setShowForm(false); setEditing(null); }}><X size={15} /></button>
          </div>
          {(!editing || editing.receipt_required) ? (
            paymentLoading ? <p>Loading deposit receipts...</p> :
              <DepositPaymentsForm key={paymentFormKey}
                initialRequired={paymentSeed?.required_deposit_amount || ''}
                initialPayments={paymentSeed?.payments || []}
                onChange={handleDepositChange} />
          ) : <div style={depPanel}>
            <span style={depTitle}>Deposit</span>
            <span style={hint}>Receipt not required (older booking)</span>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              <label style={depLbl}>Received date *<input style={depInput} type="date" value={form.transfer_date} onChange={e => setForm({ ...form, transfer_date: e.target.value })} required /><span style={hint}>sets capture month</span></label>
              {/* A text input, not type="number": the browser rejects the comma, and
                  a five-figure deposit is hard to read back unseparated. */}
              <label style={depLbl}>Amount (RM) *<input style={depInput} type="text" inputMode="decimal" value={form.deposit_amount} onChange={e => handleMoneyInput(e, next => setForm(prev => ({ ...prev, deposit_amount: next })))} placeholder="20,000.00" required /></label>
            </div>
          </div>}
          {editing?.deposit_entry_source === 'spreadsheet' && <div style={{ marginBottom: 12, fontSize: 13 }}>
            {editImportedDetails ? 'Editing booking details and deposit transactions.' : 'Attaching receipts to an imported booking. Its saved amount and date stay the same.'}
            <button type="button" style={iconBtn} onClick={() => setEditImportedDetails(value => !value)}>
              {editImportedDetails ? 'Attach receipts only' : 'Edit booking details instead'}
            </button>
          </div>}
          <fieldset disabled={editing?.deposit_entry_source === 'spreadsheet' && !editImportedDetails} style={{ border: 0, padding: 0, margin: 0 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
            <label style={lbl}>Event date<input style={inputS} type="date" value={form.event_date} onChange={e => setForm({ ...form, event_date: e.target.value })} required /></label>
            <label style={lbl}>Contract value (RM)<input style={inputS} type="number" min="0" step="0.01" value={form.contract_value} onChange={e => setForm({ ...form, contract_value: e.target.value })} required /></label>
            <label style={lbl}>Event type<select style={inputS} value={form.event_type} onChange={e => setForm({ ...form, event_type: e.target.value })}>{EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}</select></label>
            <label style={lbl}>Source<select style={inputS} value={form.lead_type} onChange={e => setForm({ ...form, lead_type: e.target.value })}><option value="lead_generate">Lead Generate</option><option value="hunting">Hunting</option></select></label>
            <label style={lbl}>Venue<select style={inputS} value={form.venue} onChange={e => setForm({ ...form, venue: e.target.value })}>{VENUES.map(v => <option key={v} value={v}>{v}</option>)}<option value={VENUE_OTHER}>{VENUE_OTHER}</option></select></label>
            {form.venue === VENUE_OTHER && <label style={lbl}>Specify venue<input style={inputS} value={form.venue_other} onChange={e => setForm({ ...form, venue_other: e.target.value })} placeholder="Venue name" /></label>}
            <label style={lbl}>Pax<input style={inputS} type="number" min="0" value={form.forecast_pax} onChange={e => setForm({ ...form, forecast_pax: e.target.value })} /></label>
            <label style={lbl}>Client<input style={inputS} value={form.client_name} onChange={e => setForm({ ...form, client_name: e.target.value })} /></label>
            {/* Optional on purpose — the number is issued after the deposit lands.
                But revenue is counted off the invoices synced from Bukku, not off this
                field, so a booking with no invoice raised never counts. */}
            <label style={lbl}>Invoice #<input style={inputS} value={form.invoice_number} onChange={e => setForm({ ...form, invoice_number: e.target.value })} /><span className="field-warn"><AlertTriangle size={13} /> Without an invoice raised, this sale will not be counted</span></label>
            <label style={lbl}>Package<input style={inputS} value={form.package} onChange={e => setForm({ ...form, package: e.target.value })} /></label>
          </div>
          </fieldset>
          <div style={{ marginTop: 12 }}><button type="submit" style={btn}>{editing?.deposit_entry_source === 'spreadsheet' && !editImportedDetails ? 'Attach receipts' : editing ? 'Save changes' : 'Create booking'}</button></div>
        </form>
      )}

      {loading ? <div style={{ color: 'var(--rmc-ink2)' }}>Loading…</div> : (<>
        {pendingBookings.length > 0 && <div className="bookings-section">
          <h4><Calendar size={18} /> Pending Delivery ({pendingBookings.length})</h4>
          <div className="table-container">
            <table className="bookings-table">
            <thead>
              {/* Paired fields share a cell — the subordinate value sits small and
                  muted beneath the primary one, matching the JQ tracker. Zen keys in
                  eleven fields; single-line columns showed only seven of them. */}
              <tr>
                <th className="col-num cell-dep">Deposit<span className="th-sub">received</span></th>
                <th>Client<span className="th-sub">invoice #</span></th>
                <th>Event<span className="th-sub">venue</span></th>
                <th>Package<span className="th-sub">type</span></th>
                <th>Source</th>
                <th className="col-num">Pax</th>
                <th className="col-num">Value RM</th>
                <th className="cell-status" title="Verification status"><span className="sr-only">Status</span></th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pendingBookings.map(b => (
                <tr key={b.id}>
                  <td className="col-num cell-dep">
                    <span className="cell-main">{formatAmount(b.deposit_amount)}</span>
                    <span className="cell-sub">{formatDateShort(b.transfer_date)}</span>
                    <DepositReceiptReview source="zen" bookingId={b.id} token={token} receiptRequired={b.receipt_required} />
                  </td>
                  <td>
                    <span className="cell-main cell-clip" title={b.client_name || ''}>{b.client_name || '—'}</span>
                    <span className="cell-sub">{b.invoice_number || '—'}</span>
                  </td>
                  <td>
                    <span className="cell-main">{formatDateShort(b.event_date)}</span>
                    {/* Venue names run long ("Kizsports & Gym @ Giant Kota Damansara") —
                        clip rather than let one row set the column's width. */}
                    <span className="cell-sub cell-clip" title={b.venue || ''}>{b.venue || '—'}</span>
                  </td>
                  <td>
                    <span className="cell-main cell-clip cell-clip-wide" title={b.package || ''}>{b.package || '—'}</span>
                    <span className="cell-sub">{b.event_type || '—'}</span>
                  </td>
                  <td>
                    <span style={{ fontSize: 11, fontWeight: 800, padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap', background: b.lead_type === 'hunting' ? 'var(--rmc-cream2)' : '#eef0f2', color: b.lead_type === 'hunting' ? '#826126' : 'var(--rmc-ink2)' }}>{leadLabel(b.lead_type)}</span>
                  </td>
                  <td className="col-num">{b.forecast_pax != null ? b.forecast_pax : '—'}</td>
                  <td className="col-num"><strong>{formatAmount(b.contract_value)}</strong></td>
                  <td className="cell-status">
                    <span
                      className={`status-dot ${b.verified ? 'is-verified' : 'is-pending'}`}
                      title={b.verified ? 'Verified' : 'Pending verification'}
                    />
                  </td>
                  <td>
                    <div className="action-buttons">
                      {isSuperadmin && !b.verified && (
                        <button className="btn-icon btn-verify" onClick={() => verifyOne(b, true)} title="Verify deposit" aria-label="Verify deposit"><Check size={15} /></button>
                      )}
                      {isSuperadmin && b.verified && (
                        <>
                          <button className="btn-icon" onClick={() => verifyOne(b, false)} title="Un-verify" aria-label="Un-verify"><Undo2 size={15} /></button>
                          <button
                            className="btn-icon btn-verify"
                            onClick={() => { setDeliverModal(b); setActualValue(formatThousands(b.contract_value)); }}
                            title="Release 30% (mark delivered)"
                            aria-label="Mark delivered"
                          ><Truck size={15} /></button>
                        </>
                      )}
                      {isZen && <button className="btn-icon" onClick={() => openEdit(b)} title="Edit" aria-label="Edit booking"><Pencil size={15} /></button>}
                      {isZen && <button className="btn-icon btn-danger" onClick={() => del(b)} title="Delete" aria-label="Delete booking"><Trash2 size={15} /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
          </div>
        </div>}

        {deliveredBookings.length > 0 && <div className="bookings-section">
          <h4><Truck size={18} /> Delivered ({deliveredBookings.length})</h4>
          <div className="table-container">
            <table className="bookings-table">
              <thead><tr>
                <th>Event Date</th><th>Client</th><th>Invoice #</th><th>Venue</th><th>Package</th>
                <th className="col-num">Deposit</th><th className="col-num">Contract Value</th><th className="col-num">Actual Value</th>
                {isZen && <th className="col-num">Rate (Locked)</th>}
                {isZen && <th className="col-num">70% Paid</th>}
                {isZen && <th className="col-num">30% Settlement</th>}
                {isZen && <th className="col-num">Total Commission</th>}
              </tr></thead>
              <tbody>{deliveredBookings.map(b => <tr key={b.id}>
                <td>{formatDateShort(b.event_date)}</td>
                <td>{b.client_name || '—'}</td>
                <td>{b.invoice_number || '—'}</td>
                <td>{b.venue || '—'}</td>
                <td className="col-package">{b.package || '—'}</td>
                <td className="col-num">{b.deposit_amount != null ? money(b.deposit_amount) : '—'}</td>
                <td className="col-num">{money(b.contract_value)}</td>
                <td className="col-num">{money(b.actual_final_value)}</td>
                {isZen && <td className="col-num">{Number(b.forecast_tier_rate || 0).toFixed(2)}%</td>}
                {isZen && <td className="col-num">{money(b.paid_70_percent)}</td>}
                {isZen && <td className={Number(b.settlement_amount) >= 0 ? 'amount-positive' : 'amount-negative'}>{money(b.settlement_amount)}</td>}
                {isZen && <td className="col-num"><strong>{money(b.final_commission)}</strong></td>}
              </tr>)}</tbody>
            </table>
          </div>
        </div>}

        {bookings.length === 0 && <div className="empty-state"><p>No bookings for this month.</p></div>}
      </>)}

      {deliverModal && (
        <div className="modal-overlay" onClick={() => setDeliverModal(null)}>
          <div className="modal-content" onClick={event => event.stopPropagation()}>
            <h4>Mark as Delivered</h4>
            <p className="modal-subtitle">
              Venue: <strong>{deliverModal.venue || '—'}</strong> | Locked Rate: <strong>{Number(deliverModal.forecast_tier_rate || 0).toFixed(2)}%</strong>
            </p>
            <div className="modal-details">
              <div className="modal-row"><span>Forecast Contract Value:</span><span>{money(deliverModal.contract_value)}</span></div>
              <div className="modal-row"><span>70% Already Paid:</span><span>{money(deliverModal.paid_70_percent)}</span></div>
            </div>
            <div className="form-group">
              <label>Actual Final Invoice Value (RM) *
                <input
                  type="text"
                  inputMode="decimal"
                  value={actualValue}
                  onChange={event => handleMoneyInput(event, setActualValue)}
                  autoFocus
                />
              </label>
            </div>
            {actualValue && (() => {
              const actualNum = parseFloat(stripThousands(actualValue));
              if (!Number.isFinite(actualNum)) return null;
              const lockedRate = Number(deliverModal.forecast_tier_rate || 0);
              const finalCommission = actualNum * lockedRate / 100;
              const settlement = finalCommission - Number(deliverModal.paid_70_percent || 0);
              return <div className="modal-preview">
                <div className="modal-row"><span>Final Commission ({lockedRate.toFixed(2)}%):</span><span>{money(finalCommission)}</span></div>
                <div className="modal-row highlight"><span>30% Settlement Due:</span><span className={settlement >= 0 ? 'amount-positive' : 'amount-negative'}>{money(settlement)}</span></div>
              </div>;
            })()}
            <div className="modal-actions">
              <button className="btn-primary" onClick={() => deliver(deliverModal)} disabled={submitting}>{submitting ? 'Processing…' : 'Confirm Delivery & Settle 30%'}</button>
              <button className="btn-secondary" onClick={() => setDeliverModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ZenBookings;
